/**
 * Help Center: "seats-and-bags" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'seats-at-checkout',
    title: 'Choosing seats at checkout',
    summary: 'On supported airlines, you pick seats at checkout on the airline’s live seat map. Any seat costs join your trip total, so you can pay for them over time.',
    keywords: ['seat selection', 'pick seats', 'seat map', 'select your seats', 'exit row', 'free seats', 'seat cost'],
    related: ['seats-and-bags/buy-seats-after-booking', 'seats-and-bags/sitting-together', 'seats-and-bags/no-seat-map', 'booking/checkout-page'],
    updated: '2026-10-05',
    body: `On supported airlines, you can pick your seats during checkout, on a live map of the plane. Any seat costs are added to your trip total, so you pay for them over time, along with the rest of your trip.

Not every airline offers a seat map, and that's okay. You can also add seats after you book. And on airlines without a seat map, our team seats your party together for you.

## What you'll see

The seat step is called "Select Your Seats", and it comes right after you choose your fare. It shows the airline's real cabin layout: a row-by-row view of the plane, with the seats that are still available.

- **Exit rows and preferred seats** appear on the map, along with standard seats.
- **Prices for your exact fare.** What a seat costs can depend on your fare, so the map shows the prices for the fare you picked.
- **Seats included with your fare** show as "Free".

## How are seat costs paid?

When you pick a seat that has a price, its cost is added to your trip total. That means it becomes part of your payment plan and is paid over time, like the rest of your trip.

You can check this before you book. The **Price Breakdown** on the checkout page shows a "Seat selection" line with your seat costs. If your seats are included with your fare, that line shows "Free".

## Should I choose now or after booking?

Either works. You can add seats any time after you book, whether you're still paying off your trip or it's paid in full. The main difference is how you pay:

| | At checkout | After booking |
|---|---|---|
| Where you choose | The "Select Your Seats" step | **Purchase Seats**, in your trip's **Flight Options** |
| How you pay | Seat costs join your trip total | Paid in full, in one card payment, with a small processing fee |
| Paid over time? | Yes, with the rest of your trip | No, it's separate from your payment plan |

So if you'd like seat costs spread across your payments, choose your seats at checkout. One more thing to know: after booking, the live seat map opens only while your trip has no seats assigned yet. [Buying seats after you book](/help/seats-and-bags/buy-seats-after-booking) walks through that option.

## Will we sit together?

Sitting together is the default, not something you need to ask for.

- **If your fare includes seat selection at no extra cost,** you can pick seats together for free here. If you don't, our team selects seats for your party at no cost: window seats, everyone together, as close to the front of the plane as possible. Seats you pick at checkout show in your trip right away, and seats our team picks show up later.
- **If your airline has no seat map,** our team arranges seats for everyone on your booking together, automatically.

[Will we be seated together?](/help/seats-and-bags/sitting-together) covers this in more detail.

## What if there's no seat step?

Whether you see a seat map depends on the airline, the route and your fare.

- **Some airlines don't offer one.** There's no live seat map for {{SEAT_MAP_UNSUPPORTED_AIRLINES}} flights. After you book, **Purchase Seats** on your trip opens a chat with the Defera Concierge, our AI concierge, so you can ask for the seats you'd like.
- **Some fares don't include choosing seats.** Basic or light fares, such as American's Basic Economy, often don't allow choosing seats in advance. When that's the case, the seat map tells you. It isn't an error: it's the airline's rule for that fare. The airline assigns seats at check-in, or you can ask us to arrange seats with the airline.

[Why there's no seat map for my flight](/help/seats-and-bags/no-seat-map) explains what to do next.

## Something not working?

If the seat step isn't showing when you expected it, or it's doing something you don't understand, [chat with the Defera Concierge](/support). It can send a request to our team to look at your checkout, so you get a real answer rather than a guess.`,
  },
  {
    slug: 'buy-seats-after-booking',
    title: 'Buying seats after you book',
    summary: 'Open your trip, tap Flight Options, then Purchase Seats. Pick seats on the live seat map and pay once by card, with a small fee, separate from your plan.',
    keywords: ['purchase seats', 'add seats later', 'seat map', 'pick seats after booking', 'paid seats', 'processing fee', 'flight options'],
    related: ['seats-and-bags/seats-at-checkout', 'seats-and-bags/no-seat-map', 'seats-and-bags/change-seats', 'rewards/miles-for-extras'],
    updated: '2026-10-05',
    body: `You can buy seats any time after you book, from your trip's **Flight Options** menu. On supported airlines, **Purchase Seats** opens the airline's live seat map, and you pay for your seats once, by card.

There's no need to wait until your trip is paid off. You can buy seats while you're still making payments, or after your trip is paid in full.

> [!NOTE] If your fare already includes seat selection and you didn't pick seats at checkout, there's nothing to buy. Our team picks your seats at no cost, and the seat numbers show up later in your trip. [When will I see my seat numbers?](/help/seats-and-bags/when-seat-numbers) explains more.

## How to buy seats

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap your trip.
2. **Open the seat map.** Tap **Flight Options**, at the top right of the trip's **Flight Details** section, then **Purchase Seats**. The airline's live seat map opens for the flights you booked.
3. **Choose your seats.** Pick seats for any or all of your travelers. The prices shown match your exact fare.
4. **Check the total.** Before you pay, you'll see the total, including a small processing fee shown as a dollar amount.
5. **Pay once, by card.** Use a saved card or a new one. Your seats show up right away in your trip's Flight Details.

## Choose for everyone at once

The live seat map can be used once per trip. As soon as seats are assigned, it closes for that trip, so choose seats for everyone you want to seat in the same purchase.

If you'd like different seats later, you can still ask. Once seats are assigned, **Change Seat Selection** appears in **Flight Options**, and our team handles the change for you. [Changing your seats](/help/seats-and-bags/change-seats) explains how.

## How does the payment work?

Buying seats this way is separate from your payment plan:

- **It's one payment.** You pay for the seats in full, by card, and it's never split into installments.
- **There's a small processing fee.** You'll see it as a dollar amount before you pay.
- **Your payment schedule doesn't change.** Your remaining payments stay exactly as they were.

For example, if you have four payments left on your trip and buy two seats today, you pay for both seats, plus the processing fee, in one card payment today. Your four remaining payments don't change.

Seats chosen at checkout work differently: their cost is added to your trip total and paid over time.

## What if the seat map doesn't open?

The live seat map isn't available on every airline, route or fare:

- **Some airlines don't offer one.** It isn't available for {{SEAT_MAP_UNSUPPORTED_AIRLINES}} flights.
- **Some fares don't include choosing seats.** Basic or light fares often don't allow choosing seats in advance.

You're not stuck either way. In those cases, **Purchase Seats** opens a chat with the Defera Concierge, our AI concierge, with your request already written. Tell it what you'd like, and it sends the request to our team. [Why there's no seat map for my flight](/help/seats-and-bags/no-seat-map) explains more.

## Can I use Defera Miles instead?

Yes, while your trip is still being paid off. On your trip page, look for the "Redeem on this trip" panel, beside or below your Payment Schedule. Its **Buy Seats** tile lets you request seats with Defera Miles, from {{MILES_SEATS_TILE}} Miles.

Tapping the tile opens a chat with the Defera Concierge with your request already written, and the concierge sends it to our team. The tile works when both of these are true:

- **You have enough Miles** for it.
- **Your trip isn't completed yet.** Once a trip is completed (paid in full), the panel reads "Save for future trips", because the Miles a trip earns go toward your next one.

[Can I use Miles for seats or bags?](/help/rewards/miles-for-extras) has the details.

## Why is Flight Options greyed out?

**Flight Options** is greyed out only when a trip is cancelled, in payment default, or every flight on it has already departed. Otherwise, you'll find it at the top right of the trip's **Flight Details** section.`,
  },
  {
    slug: 'no-seat-map',
    title: 'Why there’s no seat map for my flight',
    summary: 'Some airlines and fares don’t offer a live seat map. You’re not stuck: Purchase Seats on your trip opens the Defera Concierge, so you can ask for the seats you want.',
    keywords: ['seat map not showing', 'no seat selection', 'basic economy', 'seat request', 'choose seats', 'seats at check-in'],
    related: ['seats-and-bags/sitting-together', 'seats-and-bags/buy-seats-after-booking', 'booking/codeshare-flights', 'contact/how-requests-work'],
    updated: '2026-10-05',
    body: `Some airlines and fares don't offer a live seat map on Defera, so you can't always pick seats yourself. That doesn't mean anything is wrong with your booking, and you're not stuck.

On airlines without a seat map, our team seats everyone on your booking together, automatically. If you'd like something specific, **Purchase Seats** on your trip opens a chat with the Defera Concierge, our AI concierge, so you can ask for it.

## Why isn't there a seat map?

The live seat map isn't available on every airline, route or fare. Here's what makes the difference:

- **The airline.** There's no live seat map for {{SEAT_MAP_UNSUPPORTED_AIRLINES}} flights, at checkout or after booking.
- **Your fare.** Basic or light fares, such as American's Basic Economy, often don't allow choosing seats in advance online. When that's the case, the seat map tells you your fare doesn't include it. It's the airline's rule for that fare, not an error. The airline assigns your seats at check-in, or you can ask the Defera Concierge to have our team arrange seats with the airline.
- **A partner airline flies the plane.** A flight sold by one airline and flown by another shows as "Operated by X as Y". The seat map usually follows the airline that operates the flight. [What is a codeshare flight?](/help/booking/codeshare-flights) explains how this works.

## What happens on airlines without a seat map?

On these flights:

- **Sitting together is the default.** Our team arranges seats for everyone on your booking together, automatically. You don't need to ask.
- **You can still ask for something specific.** Tell the Defera Concierge what you'd like, such as an aisle seat or a particular row, and it sends your request to our team.

## How do I ask for specific seats?

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap your trip.
2. **Tap Purchase Seats.** Tap **Flight Options**, at the top right of the trip's **Flight Details** section, then **Purchase Seats**. When there's no live seat map, this opens a chat with the Defera Concierge, with your request already written.
3. **Say what you'd like.** For example, an aisle seat, a window seat or a particular row, and which travelers it's for.
4. **The concierge sends your request to our team.** It shows on your trip page as a card that says "Submitted · in progress".
5. **You get the answer.** When our team has resolved your request, the answer appears on that card and comes to you by email.

Already chatting with the [Defera Concierge](/support)? You can ask there directly. It's the same request.

## Can the concierge tell me which seats are open?

No. The Defera Concierge can't see which seats are still open on a flight or how full it is, so it won't guess at availability. Our team takes your request from there.`,
  },
  {
    slug: 'sitting-together',
    title: 'Will we be seated together?',
    summary: 'Yes. Sitting together is the default. Pick seats side by side when your fare includes seat selection, and where there’s no seat map, our team seats you together.',
    keywords: ['sit together', 'seats next to each other', 'family seating', 'group seats', 'adjacent seats', 'seated apart', 'travel companion'],
    related: ['seats-and-bags/seats-at-checkout', 'seats-and-bags/no-seat-map', 'seats-and-bags/change-seats', 'booking/booking-for-others'],
    updated: '2026-10-08',
    body: `Yes. Seating your party together is the default on Defera, not a special request you have to make.

We know being split up from your family or travel partner is a real worry. Here's how seating together works, and how to check the seats on your trip.

## How we seat you together

It depends on your airline and your fare:

- **Your fare includes seat selection at no extra cost.** You can pick seats together for free at checkout. If you don't, our team selects seats for your party at no cost: window seats, everyone together, as close to the front of the plane as possible. Those seat numbers show up later in your trip. [When will I see my seat numbers?](/help/seats-and-bags/when-seat-numbers) explains why.
- **Your airline has no seat map.** On {{SEAT_MAP_UNSUPPORTED_AIRLINES}} flights, our team arranges seats for everyone on your booking together, automatically. You don't need to ask.
- **You're on a basic or light fare.** These fares often don't allow choosing seats in advance. The airline assigns seats at check-in, or you can ask the Defera Concierge, our AI concierge, to have our team arrange seats with the airline.

Your booking confirmation email describes the same approach: window seats, companions together and as close to the front as possible, when the fare allows.

## How can I check our seats?

Open **Trips** and tap your trip. Any seats that are already assigned show in your trip's Flight Details.

If your seats are next to each other, you're already sitting together, and there's nothing more to do.

## Can we choose seats together ourselves?

Yes, on supported airlines:

- **At checkout:** the "Select Your Seats" step shows the airline's live seat map. Seats included with your fare show as "Free".
- **After booking:** if your trip has no seats assigned yet, tap **Flight Options**, then **Purchase Seats**, to open the live seat map. Choose seats for your whole party in the same purchase, because the seat map can be used once per trip. Seats bought this way are paid in full by card, with a small processing fee. If your fare already includes seat selection, there's nothing to buy: our team picks your seats for you at no cost.

[Buying seats after you book](/help/seats-and-bags/buy-seats-after-booking) walks through it step by step.

## Traveling with someone on another booking?

At checkout, one of the travel questions asks whether you'll be traveling with others on this flight. If they're on a separate Defera booking, answer yes, then choose **Another Defera booking**. It says "We can look for it and seat you together." You can also enter that booking's reference in the optional field below it, which helps our team find it. Once our team links your bookings, a **Traveling together** card appears at the top of your trip, and we send you an email, so you know it's on file. [The travel questions at checkout](/help/booking/concierge-travel-questions) has more.

## Want different seats?

If seats are already assigned and you'd like a change, tap **Flight Options**, then **Change Seat Selection**. Or tell the [Defera Concierge](/support) in chat. Either way, our team handles the change personally.

## Can the concierge see which seats are open?

No. The Defera Concierge can't see which seats are still open on a flight or how full it is. Where your airline offers one, the live seat map shows the seats that are available.`,
  },
  {
    slug: 'when-seat-numbers',
    title: 'When will I see my seat numbers?',
    summary: 'Airlines with a live seat map show the seats you pick at checkout right away. Otherwise our team picks your seats, and the numbers appear later in your trip.',
    keywords: ['seat numbers', 'where are we sitting', 'seats not showing', 'did you pick my seats', 'seat assignment', 'complimentary seats', 'buying from the airline', 'retail', 'seats show open', 'someone take my seats', 'seat map shows available'],
    related: ['seats-and-bags/sitting-together', 'seats-and-bags/seats-at-checkout', 'seats-and-bags/no-seat-map', 'confirmation-and-travel/when-confirmation-number'],
    updated: '2026-10-25',
    body: `It depends on how your seats were chosen. Airlines with a live seat map let you see your seat assignments right away: seats you pick on it at checkout show in your account as soon as you book. Otherwise, our team picks your seats for you, and your seat numbers are released later in your trip.

Not seeing seat numbers yet is normal. It doesn't mean anything is wrong with your trip.

## How your seats get picked

- **You picked them at checkout.** On supported airlines, the "Select Your Seats" step shows the airline's live seat map. The seats you choose there show in your trip's Flight Details right away.
- **Our team picks them.** If you didn't pick seats at checkout, our team picks them for you. When your fare includes seat selection, they're free, your whole party sits together, and we choose window seats as close to the front of the plane as we can. The seat numbers are released later in your trip.

> [!TIP] If your fare includes seat selection and you didn't pick seats at checkout, there's nothing to buy. You don't need **Purchase Seats**: our team picks your seats at no cost.

## Can I get my seat numbers sooner?

No. Seat numbers our team picks are released later in your trip, and there's no way to get them sooner. Asking again doesn't change the timing, so there's nothing you need to chase. They'll show in your trip's Flight Details once they're released.

If you'd like something specific, such as an aisle seat or a particular row, tell the [Defera Concierge](/support), our AI concierge, and it sends your preference to our team.

## Why do my seats still look open on the airline's site?

The seats on your trip are saved on your Defera trip, and they're finalized with the airline later in your trip, which can take weeks. Until then, the airline's own seat map can show those seats as open to anyone looking, including friends booking directly.

- **The process changed.** Defera used to book trips directly with the airlines, so seats showed with the airline quickly. We now work with partners that handle the booking with the airline behind the scenes, in a process built around paying over time.
- **Your group sits together.** That's guaranteed.
- **We go after the seats you chose.** When your seats are finalized, the ones you picked are what our booking system goes after. If any have been taken by then, we seat you in the closest seats together and let you know.

No airline guarantees exact seat numbers, even on tickets bought directly, but sitting together is our promise.

## Why is it different from buying from the airline?

For you, it works like any other ticket. Behind the scenes, a Defera trip is processed differently than a ticket bought at retail, directly from an airline or a travel site.

- **Your price is locked.** Your trip price is locked with Defera, and we take care of everything with the airline.
- **You're never at a disadvantage.** A Defera customer is never at a disadvantage compared with someone who bought the same flight at retail.
- **There are more moving parts.** Our systems work with partners, intermediaries and airlines in an automated, orchestrated way. That's why some things, like seat numbers our team picks and your confirmation number, come later than they would on a retail booking.

Once your confirmation number is released, you use it with the airline the same way as any other ticket: to check in, manage seats and bags and get flight updates. [When do I get my confirmation number?](/help/confirmation-and-travel/when-confirmation-number) explains when that happens.`,
  },
  {
    slug: 'change-seats',
    title: 'Changing your seats',
    summary: 'Once seats are assigned, tap Flight Options, then Change Seat Selection. Tell the Defera Concierge what you’d like, and our team handles the change personally.',
    keywords: ['change seat', 'move seats', 'different seat', 'aisle seat', 'window seat', 'exit row', 'switch seats', 'premium seat'],
    related: ['seats-and-bags/buy-seats-after-booking', 'seats-and-bags/seat-preferences', 'contact/how-requests-work', 'contact/check-request-status'],
    updated: '2026-10-05',
    body: `To change seats that are already on your trip, open your trip, tap **Flight Options**, then **Change Seat Selection**. The Defera Concierge, our AI concierge, takes your request, and our team handles the change personally.

You can ask at any time, whether you're still paying off your trip or it's already paid in full.

## Why does a person handle seat changes?

You don't change seats yourself in the app, and that's on purpose. It lets our team look at the airline's seat map themselves and pick the best seats available for you.

## How do I ask for a change?

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap your trip.
2. **Tap Change Seat Selection.** Tap **Flight Options**, at the top right of the trip's **Flight Details** section, then **Change Seat Selection**.
3. **Confirm.** A chat with the Defera Concierge opens, with your request already written.
4. **Tell it what you'd like.** For example, a different row, an aisle or window seat, an exit row or a premium seat, and which travelers it's for.
5. **The concierge sends your request to our team.** It shows on your trip page as a card that says "Submitted · in progress".

Already chatting with the [Defera Concierge](/support)? You can ask there directly. It's the same request, so there's no need to tap the button as well.

## What should I include?

A clear request helps our team find the right seats. It's worth mentioning:

- **Who it's for.** Which travelers you'd like moved.
- **Which flights.** For example, your outbound flight, your return, or both.
- **What matters most.** For example, sitting together, an aisle seat, or being near the front.

## What happens next?

While our team works on your request, the card on your trip page shows "Submitted · in progress". When it's resolved, the answer appears on that card, and you get it by email too. The card stays on your trip page until you dismiss it.

[How requests to our team work](/help/contact/how-requests-work) has more on what to expect.

## Can the concierge see which seats are open?

No. The Defera Concierge can't see live seat availability or how full a flight is, so it won't guess. Our team looks at the airline's seat map when it handles your request.

## Don't see Change Seat Selection?

**Change Seat Selection** appears only once seats are assigned. Before that, **Flight Options** shows **Purchase Seats**, which lets you pick seats yourself on the live seat map, where your airline offers one. [Buying seats after you book](/help/seats-and-bags/buy-seats-after-booking) explains how.

If **Flight Options** is greyed out, the trip is cancelled, in payment default, or every flight on it has already departed.`,
  },
  {
    slug: 'seat-preferences',
    title: 'Window or aisle? How seat preferences work',
    summary: 'By default, our team picks window seats together, near the front, when your fare allows. Prefer an aisle? Choose your own seats or ask us to change them.',
    keywords: ['aisle seat', 'window seat', 'seat preference', 'front of the plane', 'exit row', 'preferred seating', 'middle seat'],
    related: ['seats-and-bags/sitting-together', 'seats-and-bags/change-seats', 'seats-and-bags/seats-at-checkout', 'rewards/defera-status'],
    updated: '2026-10-05',
    body: `When your fare includes seat selection, our team chooses seats for your party by default: window seats, everyone together, and as close to the front of the plane as possible.

Prefer something else, like an aisle seat or a particular row? You have options. You can pick your own seats where your airline offers a live seat map, or ask us to change seats that are already assigned.

## How do you choose seats for us?

When your fare includes seat selection at no extra cost, our team aims for:

- window seats for your party
- everyone seated together
- seats as close to the front of the plane as possible

Your booking confirmation email describes the same approach, so you'll see it there too.

## How can I get the seats I prefer?

It depends on where you are with your trip:

- **At checkout:** on supported airlines, the "Select Your Seats" step shows the airline's live seat map, including exit rows and preferred seats, with prices that match your fare. Any seat costs join your trip total and are paid over time.
- **After booking, before seats are assigned:** on supported airlines, tap **Flight Options**, then **Purchase Seats**, to open the live seat map. You pay once by card, with a small processing fee, separately from your payment plan.
- **After seats are assigned:** tap **Flight Options**, then **Change Seat Selection**, and tell the Defera Concierge, our AI concierge, what you'd prefer. That could be an aisle or window seat, a different row, an exit row or a premium seat. Our team handles the change personally.

[Changing your seats](/help/seats-and-bags/change-seats) walks through that last option.

## What if we each want something different?

When you tell the Defera Concierge what you'd like, say which traveler each preference is for. For example, a window seat for one traveler and an aisle seat for another.

## What if there's no seat map for my flight?

There's no live seat map for {{SEAT_MAP_UNSUPPORTED_AIRLINES}} flights. On these flights, our team arranges seats for everyone on your booking together, automatically.

To share a preference, open your trip and tap **Flight Options**, then **Purchase Seats**. A chat with the Defera Concierge opens with your request already written. Tell it what you'd like, and it sends the request to our team. [Why there's no seat map for my flight](/help/seats-and-bags/no-seat-map) has more.

## Can my Defera Status help?

Preferred seating is one of the perks that higher Defera Status tiers can unlock, so it depends on your tier. Defera Status is our tier program, and your tier is based on your qualifying spend on completed trips in a calendar year.

The program guide on the **Rewards** page shows what each tier includes. [Defera Status tiers](/help/rewards/defera-status) gives an overview.`,
  },
  {
    slug: 'bags-included',
    title: 'What bags are included with my fare?',
    summary: 'It depends on your fare and airline. Open Purchase Baggage on your trip to see if checked bags are included, and ask the Defera Concierge about anything else.',
    keywords: ['baggage allowance', 'checked bag', 'carry-on', 'luggage', 'suitcase', 'free bags', 'bag fees'],
    related: ['seats-and-bags/add-checked-bags', 'seats-and-bags/charged-for-bags-at-airport', 'booking/codeshare-flights', 'rewards/defera-status'],
    updated: '2026-10-05',
    body: `The bags that come with your trip depend on your fare and your airline. To see whether checked bags are included, open **Purchase Baggage** in your trip's **Flight Options**. If your fare already includes them, it tells you so.

Airlines set their own baggage rules, and those rules can differ from one fare to another, even on the same flight. Some fares include checked bags, and others don't. Here's how to find out what yours includes, and where to ask about anything else.

## How do I check?

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap your trip.
2. **Open Purchase Baggage.** Tap **Flight Options**, at the top right of the trip's **Flight Details** section, then **Purchase Baggage**.
3. **See what your fare includes.** If your fare already includes checked bags, the flow says so. If not, it shows live prices for adding checked bags for each traveler.

Opening it doesn't charge you anything. You pay only if you choose bags and pay for them.

If live bag pricing isn't available for your fare, the flow says that too, and offers to connect you with the Defera Concierge, our AI concierge.

## What can Purchase Baggage tell me?

Purchase Baggage is for checked bags. It can tell you:

- **Whether checked bags are included** with your fare.
- **What it costs to add checked bags,** for each traveler, when live pricing is available for your fare.

## What about carry-on bags?

For carry-on bags, or anything else about your fare's baggage rules, ask the [Defera Concierge](/support). It can send a request to our team to check your specific fare. The answer appears on your trip page and comes to you by email.

## What if a partner airline flies the plane?

A flight sold by one airline and flown by another shows as "Operated by X as Y". The airline that operates the flight usually sets the baggage rules, and it's also the one whose check-in desk you'll use. [What is a codeshare flight?](/help/booking/codeshare-flights) has more.

## Need more bags?

You can add checked bags any time after booking, whether you're still paying off your trip or it's paid in full. You pay for them by card, in one payment, separately from your payment plan. [Adding checked bags](/help/seats-and-bags/add-checked-bags) walks you through it.

## Can my Defera Status help?

Checked bags are one of the perks that higher Defera Status tiers can unlock, so it depends on your tier. Defera Status is our tier program, and your tier is based on your qualifying spend on completed trips in a calendar year.

The program guide on the **Rewards** page shows what each tier includes. [Defera Status tiers](/help/rewards/defera-status) gives an overview.`,
  },
  {
    slug: 'add-checked-bags',
    title: 'Adding checked bags',
    summary: 'Open your trip, tap Flight Options, then Purchase Baggage. Choose bags for each traveler, see live prices and pay by card in one go, separate from your plan.',
    keywords: ['add luggage', 'buy bags', 'purchase baggage', 'extra bag', 'checked luggage', 'suitcase', 'bag fee'],
    related: ['seats-and-bags/bags-included', 'seats-and-bags/charged-for-bags-at-airport', 'rewards/miles-for-extras', 'contact/how-requests-work'],
    updated: '2026-10-05',
    body: `You can add checked bags any time after you book. Open your trip, tap **Flight Options**, then **Purchase Baggage**. You choose bags for each traveler, see live prices, and pay by card in one go.

There's no need to wait until your trip is paid off. You can add bags while you're still making payments or after your trip is paid in full, and your payment plan stays as it is.

## How do I add checked bags?

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap your trip.
2. **Open Purchase Baggage.** Tap **Flight Options**, at the top right of the trip's **Flight Details** section, then **Purchase Baggage**.
3. **Choose your bags.** Pick the checked bags you want for each traveler.
4. **Check the price.** You'll see live prices before you pay.
5. **Pay by card, in one payment.** All the bags are paid for at once.

## How does the payment work?

Bags are paid for separately from your payment plan:

- **It's one payment.** You pay for all the bags by card, at once.
- **It isn't split up.** The cost isn't spread across your installments.
- **Your schedule stays the same.** Your remaining payments don't change.

For example, if you add one checked bag for each of two travelers, you see the live price for both bags and pay for them together, in one card payment. Your remaining payments stay exactly as they were.

## What if the flow offers the concierge instead?

Sometimes Purchase Baggage offers to connect you with the Defera Concierge, our AI concierge, instead of showing prices. The flow tells you when this is the case. It can happen when:

- your fare already includes checked bags
- live bag prices aren't available for your fare

If you're connected, tell the concierge which travelers need bags and how many. It sends the request to our team, and the request shows on your trip page as a card that says "Submitted · in progress". When it's resolved, the answer appears on that card and comes to you by email.

## Can I use Defera Miles instead?

Yes, while your trip is still being paid off. On your trip page, look for the "Redeem on this trip" panel, beside or below your Payment Schedule. Its **Add Baggage** tile lets you request bags with Defera Miles, from {{MILES_BAGS_TILE}} Miles.

Tapping the tile opens a chat with the Defera Concierge with your request already written, and the concierge sends it to our team. The tile works when both of these are true:

- **You have enough Miles** for it.
- **Your trip isn't completed yet.** Once a trip is completed (paid in full), the panel reads "Save for future trips", because the Miles a trip earns go toward your next one.

[Can I use Miles for seats or bags?](/help/rewards/miles-for-extras) has the details.

## What about carry-on bags?

Purchase Baggage is for checked bags. For questions about carry-on bags, or anything else about your fare's baggage rules, ask the [Defera Concierge](/support).

## Why is Flight Options greyed out?

**Flight Options** is greyed out only when a trip is cancelled, in payment default, or every flight on it has already departed.`,
  },
  {
    slug: 'charged-for-bags-at-airport',
    title: 'I was charged for bags at the airport',
    summary: 'If you think a bag charge at the airport was wrong, gather your receipt and booking details and contact us. Our team will look into it and reply by email.',
    keywords: ['baggage fee', 'bag fee at check-in', 'airline charged me', 'luggage charge', 'checked bag charge', 'airport fee', 'overcharged'],
    related: ['seats-and-bags/bags-included', 'seats-and-bags/add-checked-bags', 'contact/how-to-contact', 'contact/how-requests-work'],
    updated: '2026-10-23',
    body: `If an airline charged you for bags at the airport and you don't think it should have, contact us so our team can look into it. Before you do, gather your receipt and your booking details.

We know an unexpected charge at the airport is frustrating, especially when you're trying to catch a flight. We can't promise an outcome in advance, but having everything ready helps our team look at your trip and give you a clear answer.

## What should I gather?

- **The receipt for the bag charge.** Note the amount, the date, the airport, and which traveler and how many bags it covered.
- **Your Defera booking reference.** It's at the top of your booking confirmation email. It's also on your [Trip Statement](/help/payments/trip-statement), which you can download with the download icon at the top of your trip page.
- **Your airline confirmation number,** if your trip has one. It's the code the airline uses to find your reservation.
- **Whether you added bags with Purchase Baggage** before you flew, and roughly when.
- **Anything the airline told you** about the charge.

## How do I contact you?

Choose whichever suits you best:

- **Chat** with the [Defera Concierge](/support), our AI concierge. Share the details above, and it can send a request to our team.
- **Send us a message** from the [Contact page](/contact#message) with the details above, including what your receipt shows. Our team replies within 24 hours.
- **Call** [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}). The Defera Concierge answers by voice, 24/7. It's the same AI concierge as the chat, so use whichever you prefer.

## What happens next?

When the Defera Concierge sends a request to our team, it shows on your trip page as a card that says "Submitted · in progress". When our team has an answer, it appears on that card, and you get it by email too.

The card stays on your trip page until you dismiss it, so you can come back to the answer whenever you need it.

## For your next trip

You can check what your fare includes, and add checked bags any time after booking, from your trip's **Flight Options**. [What bags are included with my fare?](/help/seats-and-bags/bags-included) shows you how.`,
  },
  {
    slug: 'upgrades',
    title: 'How upgrades work on Defera',
    summary: 'There are three kinds: a faster route at no cost (Free Flight Upgrade), a complimentary cabin upgrade when we can arrange one, and a cabin upgrade you request.',
    keywords: ['cabin upgrade', 'business class', 'first class', 'free upgrade', 'faster route', 'upgraded email', 'complimentary upgrade', 'free flight upgrade'],
    related: ['booking/free-flight-upgrade-question', 'rewards/miles-for-extras', 'seats-and-bags/change-seats', 'emails/emails-we-send'],
    updated: '2026-10-25',
    body: `There are three kinds of upgrade on Defera, and they work quite differently. Two of them cost you nothing. The third is a better cabin that you ask for, paid or with Defera Miles.

Here's how they compare:

| Upgrade | What you get | What it costs |
|---|---|---|
| Free Flight Upgrade | A faster route, in the same cabin | Nothing |
| Complimentary cabin upgrade | A better cabin, when our team can arrange one | Nothing |
| Cabin upgrade you request | A better cabin | Paid, or with Defera Miles |

In short, a better cabin either comes as a thank-you from our team, or it's something you ask for. The Free Flight Upgrade is about a faster route, not a better cabin.

## Free Flight Upgrade: a faster route, same cabin

Every booking answers a required question at checkout: if our team spots a faster route to your destination, with a shorter total trip time or a shorter layover, may we move you to it? If you say yes, you also choose whether the new route can be on any airline or only on the airline you booked.

"No" is a perfectly normal answer, and there's no downside to it. [The Free Flight Upgrade question at checkout](/help/booking/free-flight-upgrade-question) has more.

If you say yes, here's what we promise:

- **Same cabin.** Despite the name, it isn't a cabin upgrade. Economy stays economy, and you keep the same fare. It's called an upgrade because the route is better, not the class of service.
- **Same travel dates,** with a departure near your original time.
- **No extra cost, ever.** If a faster route costs more, you don't pay the difference.
- **Only for a real improvement:** a shorter total trip time or a shorter layover.
- **We email you if we make a change.** You're never moved without being told.

## Will my flight change if I said yes?

Not unless we find a real improvement. Saying yes is permission to look, not a plan to move you. Most bookings are never changed, and it may never come up.

Saying yes doesn't change your fare rules, refundability, seats you've paid for, or your payment plan. If a change ever affected seats you'd paid for, we'd sort that out with you first.

## Can I change my answer?

Yes, either way. Ask the [Defera Concierge](/support), our AI concierge, and it sends the change to our team.

## Complimentary cabin upgrades

When our team can, it arranges a complimentary cabin upgrade on a booking, as a thank-you for booking with Defera. Upgrades are subject to availability, and eligibility is assessed per booking. They're never guaranteed and aren't offered on every trip, and there's nothing to apply for or claim.

When one is arranged, you'll hear about it in two ways:

- **A card on your trip page.** It says "Complimentary Upgrade" and which cabin you've been upgraded to. You'll find it below your trip's progress bar.
- **An email.** A "You've been upgraded" email arrives as soon as it's arranged.

The upgrade is reflected on your boarding pass at check-in. There's nothing to do and no cost, and it doesn't change your fare, refundability or payment plan.

### Other complimentary extras

Besides a cabin upgrade, our team sometimes arranges another extra as a thank-you, such as priority boarding, an extra-legroom seat, a checked bag, a lounge pass, Wi-Fi, or food and drinks. The same goes for these: they're never guaranteed, and there's nothing to apply for or claim.

When one is arranged, you'll see a **Complimentary** card on your trip page and get a "We've added … to your trip" email. There's nothing to do and no cost: you'll see it when you check in, and it doesn't change your fare or payment plan.

## How do I ask for a better cabin?

If you'd like a better cabin, you can request a cabin upgrade, paid or with Defera Miles. Ask the Defera Concierge, and it sends the request to our team. The answer appears on your trip page and comes to you by email.

To use Miles, you can also tap the **Cabin Upgrade** tile in the "Redeem on this trip" panel on your trip page, beside or below your Payment Schedule. It starts from {{MILES_UPGRADE_TILE}} Miles. Tapping it opens a chat with the Defera Concierge with your request already written.

The tile works while your trip is still being paid off and you have enough Miles. Once a trip is completed (paid in full), the panel reads "Save for future trips", because the Miles a trip earns go toward your next one. [Can I use Miles for seats or bags?](/help/rewards/miles-for-extras) covers the tiles in more detail.`,
  },
  {
    slug: 'frequent-flyer-and-precheck',
    title: 'Frequent flyer numbers and TSA PreCheck',
    summary: 'Add frequent flyer and Known Traveler Numbers with each traveler’s details at checkout, or save them in your Profile so future bookings fill them in for you.',
    keywords: ['known traveler number', 'KTN', 'Global Entry', 'loyalty number', 'airline miles', 'MileagePlus', 'AAdvantage', 'SkyMiles'],
    related: ['account/saved-passengers', 'booking/traveler-information', 'booking/codeshare-flights', 'rewards/how-miles-work'],
    updated: '2026-10-05',
    body: `You can add frequent flyer numbers and a TSA PreCheck, Global Entry or Known Traveler Number for each traveler. They're optional. Enter them with each traveler's details at checkout, or save them to a traveler in your **Profile** so future bookings fill them in for you.

All major airline loyalty programs are supported, including MileagePlus, AAdvantage, SkyMiles, Executive Club, Skywards and Miles & More.

## What are these numbers?

- **A frequent flyer number** is your membership number in an airline's loyalty program. Adding it to your booking connects your flight to your account with that program.
- **A Known Traveler Number** is the number you get as a member of a program like TSA PreCheck or Global Entry. Adding it to your booking is how your membership is matched to your flight.

## How do I add them at checkout?

When you enter each traveler's details at checkout, frequent flyer numbers and the Known Traveler Number are optional. If you've saved the traveler in your Profile, choose them from the dropdown, and their saved details fill in.

## How do I save them for future bookings?

Here's how:

1. **Open your Profile.** You'll find **Profile** in the avatar menu.
2. **Choose the traveler.** In the Passengers section, tap **Add** at the top right to add a traveler, or the edit icon next to a traveler you've saved.
3. **Enter their numbers.** Add their frequent flyer numbers and their TSA PreCheck, Global Entry or Known Traveler Number, along with their other details.
4. **Save your changes.** The next time you book, choose the traveler from the dropdown, and their saved details fill in.

[Saving travelers to your profile](/help/account/saved-passengers) has more on managing your saved travelers.

## How can I check the numbers on my booking?

Your booking confirmation email shows only the last 4 digits of Known Traveler Numbers. To see the full numbers, open **Trips**, tap your trip, and look in Flight Details.

## Already booked without them?

To add or correct a number on a trip you've already booked, ask the [Defera Concierge](/support), our AI concierge. It can send a request to our team, and the answer appears on your trip page and comes to you by email.

## Which program earns miles on a partner flight?

Some flights are sold by one airline and flown by another, and show as "Operated by X as Y". On those flights, the airline that sold the seat typically decides which loyalty program earns the miles. [What is a codeshare flight?](/help/booking/codeshare-flights) explains more.

## Good to know

- **Defera Miles are separate.** They're Defera's own rewards, earned on completed trips, and they're not the same as the miles you earn with an airline.
- **Questions about TSA PreCheck itself,** such as how to apply, are best answered by the TSA at [tsa.gov](https://www.tsa.gov).`,
  },
];
